import {Component, OnInit} from '@angular/core';
import {FormGroup} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {Title} from '@angular/platform-browser';
import {MenuItem, MessageService} from 'primeng/api';
import {
  OfferTextSelectOption,
  OfferTextVehicleCondition
} from './offer-text.models';
import {OfferTextFormService} from './offer-text-form.service';
import {NavigationService} from '../../../../../service/navigation.service';
import {AdTypesSettingsService} from '../../../../../service/ad-types-settings.service';

const accountIdRadix = 10;
const defaultAccountId = 0;
const failedLoadSettingsDetail = 'Failed to load offer sets settings';
const failedSaveSettingsDetail = 'Failed to save offer sets settings';
const navigationConfigurationFile = 'settings-view-side-menu.configuration.json';
const newVehicleCondition: OfferTextVehicleCondition = 'new';
const offerSetsPageTitle = 'Offer Sets';
const savedSettingsDetail = 'Offer Sets settings updated';
const usedVehicleCondition: OfferTextVehicleCondition = 'used';
const validationErrorDetail = 'Please fix validation errors before saving';

@Component({
  selector: 'auto-offer-text',
  templateUrl: './offer-text.component.html',
  styleUrls: ['./offer-text.component.scss'],
  standalone: false
})
export class OfferTextComponent implements OnInit {
  accountId = defaultAccountId;
  newVehicleCondition = newVehicleCondition;
  settingsSideNav: MenuItem[] = [];
  taglineTypeOptions: OfferTextSelectOption[] = [
    {label: 'Marquee', value: 'marquee'},
    {label: 'Static', value: 'static'}
  ];
  usedVehicleCondition = usedVehicleCondition;
  useDefaultTaglineOptions: OfferTextSelectOption[] = [
    {label: 'Always use Default Tagline', value: '1'},
    {label: 'Use Default Tagline as a fallback', value: '0'}
  ];
  formGroup!: FormGroup;
  isLoadingOfferTextSettings = false;
  isSaving = false;

  constructor(
    private titleService: Title,
    private route: ActivatedRoute,
    private navigationService: NavigationService,
    private offerTextFormService: OfferTextFormService,
    private adTypesSettingsService: AdTypesSettingsService,
    private messageService: MessageService
  )
  {
    this.formGroup = this.offerTextFormService.formGroup;
  }

  /**
   * The nginit entry point.
   */
  ngOnInit(): void
  {
    this.titleService.setTitle(offerSetsPageTitle);
    this.route.paramMap.subscribe((params) => {
      const accountIdParam = params.get('accountId') ?? defaultAccountId.toString();
      const parsedAccountId = Number.parseInt(accountIdParam, accountIdRadix);
      this.accountId = defaultAccountId;

      if (!Number.isNaN(parsedAccountId)) {
        this.accountId = parsedAccountId;
      }

      this.initNavigation();
      this.loadOfferTextSettings();
    });
  }

  /**
   * Initialize the side navigation menu.
   */
  initNavigation(): void
  {
    this.navigationService.getMenuItems(this.accountId, navigationConfigurationFile)
      .then((navItems) => {
        this.settingsSideNav = navItems;
      });
  }

  /**
   * Checks whether the Save button should be disabled.
   */
  getSaveButtonDisabled(): boolean
  {
    let isDisabled = true;

    if (this.offerTextFormService.isDirty() && this.offerTextFormService.isValid() && this.isValidAccountId() && !this.isLoadingOfferTextSettings) {
      isDisabled = false;
    }

    return isDisabled;
  }

  /**
   * Saves offer set visible text settings for this account.
   */
  saveSettings(): void
  {
    let canSave = !this.getSaveButtonDisabled();

    if (!this.offerTextFormService.isValid()) {
      this.offerTextFormService.markAllAsTouched();
      this.showSaveValidationError();
      canSave = false;
    }

    if (canSave) {
      this.persistOfferTextSettings();
    }
  }

  /**
   * Selects the new condition form group used by the visible text panel.
   */
  get newConditionFormGroup(): FormGroup
  {
    return this.formGroup.controls[newVehicleCondition] as FormGroup;
  }

  /**
   * Selects the used condition form group used by the visible text panel.
   */
  get usedConditionFormGroup(): FormGroup
  {
    return this.formGroup.controls[usedVehicleCondition] as FormGroup;
  }

  /**
   * Loads offer set visible text settings for this account.
   */
  private loadOfferTextSettings(): void
  {
    if (this.isValidAccountId()) {
      this.isLoadingOfferTextSettings = true;
      this.adTypesSettingsService.getOfferTextSettings(this.accountId).subscribe({
        next: (settingsPayload) => {
          this.offerTextFormService.hydrate(settingsPayload);
        },
        error: () => {
          this.handleLoadError();
        },
        complete: () => {
          this.isLoadingOfferTextSettings = false;
        }
      });
    }
  }

  /**
   * Persists visible text settings using the existing Offer Sets endpoint.
   */
  private persistOfferTextSettings(): void
  {
    this.isSaving = true;
    const payload = this.offerTextFormService.buildPayload(this.accountId);

    this.adTypesSettingsService.saveOfferTextSettings(payload).subscribe({
      next: () => {
        this.handleSaveSuccess();
      },
      error: () => {
        this.handleSaveError();
      },
      complete: () => {
        this.handleSaveComplete();
      }
    });
  }

  /**
   * Checks whether the route account ID can be used by account settings calls.
   */
  private isValidAccountId(): boolean
  {
    const isValidAccountId = Number.isInteger(this.accountId) && this.accountId > defaultAccountId;

    return isValidAccountId;
  }

  /**
   * Handles failed initial settings load.
   */
  private handleLoadError(): void
  {
    this.isLoadingOfferTextSettings = false;
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: failedLoadSettingsDetail
    });
  }

  /**
   * Handles successful saves.
   */
  private handleSaveSuccess(): void
  {
    this.offerTextFormService.markAsPristine();
    this.messageService.add({
      severity: 'success',
      summary: 'Saved',
      detail: savedSettingsDetail
    });
  }

  /**
   * Handles failed saves.
   */
  private handleSaveError(): void
  {
    this.isSaving = false;
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: failedSaveSettingsDetail
    });
  }

  /**
   * Handles save completion.
   */
  private handleSaveComplete(): void
  {
    this.isSaving = false;
  }

  /**
   * Shows validation feedback when save is requested with invalid data.
   */
  private showSaveValidationError(): void
  {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: validationErrorDetail
    });
  }
}
